import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import ListActions from 'Components/ListActions'
import PageTitle from 'Components/PageTitle'
import { ParameterGroups } from 'Services/ProductManagement/Features/Parameter/Components/ParameterGroups'
import { useParameterGroups } from 'Services/ProductManagement/Features/Parameter/Hooks'
import type { ParameterGroupCategory } from 'Services/ProductManagement/Features/Parameter/types'
import { TAB_PARAM_KEY } from 'Services/ProductManagement/Features/Parameter/Util/constants'
import {
  ALLOWED_TOKENS_CATEGORY,
  CUSTOMER_GROUP_CATEGORY,
  DOMICILE_CLASS_CATEGORY,
  PRODUCT_GROUP_CATEGORY,
  PRODUCT_MANAGEMENT_ROOT_URL,
  SALES_CHANNEL_CATEGORY
} from 'Util/constants'
import AddIcon from '@mui/icons-material/Add'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Tab } from '@mui/material'
import Box from '@mui/material/Box'
import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

function ParameterListPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/settings/product-parameters`
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromParams = searchParams.get(TAB_PARAM_KEY) ?? PRODUCT_GROUP_CATEGORY

  const {
    productGroupParameterGroup,
    isProductGroupParameterGroupLoading,
    fetchProductGroups,
    customerGroupParameterGroup,
    isCustomerGroupParameterGroupLoading,
    fetchCustomerGroups,
    salesChannelParameterGroup,
    isSalesChannelParameterGroupLoading,
    fetchSalesChannels,
    allowedTokensParameterGroup,
    isAllowedTokensParameterGroupLoading,
    fetchAllowedTokens,
    domicileClassParameterGroup,
    isDomicileClassParameterGroupLoading,
    fetchDomicileClasses
  } = useParameterGroups()

  useEffect(() => {
    switch (tabFromParams) {
      case PRODUCT_GROUP_CATEGORY:
        fetchProductGroups()
        break
      case CUSTOMER_GROUP_CATEGORY:
        fetchCustomerGroups()
        break
      case SALES_CHANNEL_CATEGORY:
        fetchSalesChannels()
        break
      case ALLOWED_TOKENS_CATEGORY:
        fetchAllowedTokens()
        break
      case DOMICILE_CLASS_CATEGORY:
        fetchDomicileClasses()
    }
  }, [tabFromParams])

  const handleTabChange = (_event: React.SyntheticEvent, newValue: ParameterGroupCategory) => {
    searchParams.set(TAB_PARAM_KEY, newValue)
    setSearchParams(searchParams)
  }

  const breadcrumbs = [
    {
      title: t`Settings`
    },
    {
      title: t`Product parameters`,
      link: baseUrl
    }
  ]

  const labelFromTab = (selectedTab: string) => {
    switch (selectedTab) {
      case PRODUCT_GROUP_CATEGORY:
        return t`Product parameter group`
      case CUSTOMER_GROUP_CATEGORY:
        return t`Customer group`
      case SALES_CHANNEL_CATEGORY:
        return t`Sales channel`
      case ALLOWED_TOKENS_CATEGORY:
        return t`Allowed token`
      case DOMICILE_CLASS_CATEGORY:
        return t`Domicile class`
    }
  }

  const addNewParameterTitle = t`Add new` + ' ' + labelFromTab(tabFromParams)?.toLowerCase()

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <PageTitle>{t`Product parameters`}</PageTitle>
        <ListActions>
          <CustomButton
            onClick={() => navigate(`${baseUrl}/${tabFromParams}/create`)}
            size="small"
            startIcon={<AddIcon />}
          >
            {addNewParameterTitle}
          </CustomButton>
        </ListActions>
      </Box>
      <TabContext value={tabFromParams}>
        <TabList onChange={handleTabChange} aria-label={t`Product parameter tab list`}>
          <Tab label={t`Product groups`} value={PRODUCT_GROUP_CATEGORY} />
          <Tab label={t`Customer groups`} value={CUSTOMER_GROUP_CATEGORY} />
          <Tab label={t`Sales channels`} value={SALES_CHANNEL_CATEGORY} />
          <Tab label={t`Allowed tokens`} value={ALLOWED_TOKENS_CATEGORY} />
          <Tab label={t`Domicile classes`} value={DOMICILE_CLASS_CATEGORY} />
        </TabList>
        <Box pt={1}>
          <TabPanel sx={{ p: 0 }} value={PRODUCT_GROUP_CATEGORY}>
            <ParameterGroups
              singleParameterGroup={productGroupParameterGroup}
              isLoading={isProductGroupParameterGroupLoading}
            />
          </TabPanel>
          <TabPanel sx={{ p: 0 }} value={CUSTOMER_GROUP_CATEGORY}>
            <ParameterGroups
              singleParameterGroup={customerGroupParameterGroup}
              isLoading={isCustomerGroupParameterGroupLoading}
            />
          </TabPanel>
          <TabPanel sx={{ p: 0 }} value={SALES_CHANNEL_CATEGORY}>
            <ParameterGroups
              singleParameterGroup={salesChannelParameterGroup}
              isLoading={isSalesChannelParameterGroupLoading}
            />
          </TabPanel>
          <TabPanel sx={{ p: 0 }} value={ALLOWED_TOKENS_CATEGORY}>
            <ParameterGroups
              singleParameterGroup={allowedTokensParameterGroup}
              isLoading={isAllowedTokensParameterGroupLoading}
            />
          </TabPanel>
          <TabPanel sx={{ p: 0 }} value={DOMICILE_CLASS_CATEGORY}>
            <ParameterGroups
              singleParameterGroup={domicileClassParameterGroup}
              isLoading={isDomicileClassParameterGroupLoading}
            />
          </TabPanel>
        </Box>
      </TabContext>
    </Box>
  )
}

export default ParameterListPage
